<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { BaseTable } from 'dashboard/components-next/table';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import NewWebhook from './NewWebHook.vue';
import EditWebhook from './EditWebHook.vue';
import WebhookRow from './WebhookRow.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
    BaseTable,
    NewWebhook,
    EditWebhook,
    WebhookRow,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },
  data() {
    return {
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedWebHook: {},
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      records: 'webhooks/getWebhooks',
      uiFlags: 'webhooks/getUIFlags',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    apiAndWebhooksEnabled() {
      return (
        !this.isOnChatwootCloud ||
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.API_AND_WEBHOOKS
        )
      );
    },
    integration() {
      return this.$store.getters['integrations/getIntegration']('webhook');
    },
    filteredRecords() {
      const query = this.searchQuery.trim();
      if (!query) return this.records;
      return picoSearch(this.records, query, ['name', 'url']);
    },
    tableHeaders() {
      return [
        this.$t(
          'INTEGRATION_SETTINGS.WEBHOOK.LIST.TABLE_HEADER.WEBHOOK_ENDPOINT'
        ),
        this.$t('INTEGRATION_SETTINGS.WEBHOOK.LIST.TABLE_HEADER.ACTIONS'),
      ];
    },
  },
  watch: {
    apiAndWebhooksEnabled: {
      immediate: true,
      handler(enabled) {
        if (enabled) this.$store.dispatch('webhooks/get');
      },
    },
  },
  mounted() {
    this.$store.dispatch('integrations/get', 'webhook');
  },
  methods: {
    // One drawer at a time: the open one lets go first (asks when it has changes).
    guarded(action) {
      const drawer = this.$refs.drawerRef;
      return drawer ? drawer.runGuarded(action) : action();
    },
    openAddPopup() {
      this.guarded(() => {
        this.showEditPopup = false;
        this.showAddPopup = true;
      });
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedWebHook = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    openEditPopup(webhook) {
      this.guarded(() => {
        this.showAddPopup = false;
        this.selectedWebHook = webhook;
        this.showEditPopup = true;
      });
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    confirmDeletion() {
      this.closeDeletePopup();
      this.deleteWebhook(this.selectedWebHook.id);
    },
    async deleteWebhook(id) {
      try {
        await this.$store.dispatch('webhooks/delete', id);
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="apiAndWebhooksEnabled && uiFlags.fetchingList"
    :loading-message="$t('INTEGRATION_SETTINGS.WEBHOOK.LOADING')"
    :no-records-message="$t('INTEGRATION_SETTINGS.WEBHOOK.LIST.404')"
    :no-records-found="apiAndWebhooksEnabled && !records.length"
  >
    <template #header>
      <BaseSettingsHeader
        v-if="integration.name"
        v-model:search-query="searchQuery"
        :title="integration.name"
        :description="replaceInstallationName(integration.description)"
        :link-text="$t('INTEGRATION_SETTINGS.WEBHOOK.LEARN_MORE')"
        :search-placeholder="
          apiAndWebhooksEnabled
            ? $t('INTEGRATION_SETTINGS.WEBHOOK.SEARCH_PLACEHOLDER')
            : ''
        "
        feature-name="webhook"
      >
        <template v-if="apiAndWebhooksEnabled && records?.length" #count>
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.COUNT', { n: records.length }) }}
        </template>
        <template v-if="apiAndWebhooksEnabled" #actions>
          <NextButton
            blue
            :label="$t('INTEGRATION_SETTINGS.WEBHOOK.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template v-if="apiAndWebhooksEnabled" #drawer>
      <NewWebhook
        v-if="showAddPopup"
        ref="drawerRef"
        :on-close="hideAddPopup"
      />
      <EditWebhook
        v-if="showEditPopup"
        :id="selectedWebHook.id"
        :key="selectedWebHook.id"
        ref="drawerRef"
        :value="selectedWebHook"
        :on-close="hideEditPopup"
      />
    </template>
    <template v-if="apiAndWebhooksEnabled" #emptyActions>
      <NextButton
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.HEADER_BTN_TXT')"
        size="sm"
        outline
        @click="openAddPopup"
      />
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery ? $t('INTEGRATION_SETTINGS.WEBHOOK.NO_RESULTS') : ''
        "
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template #row="{ items }">
          <WebhookRow
            v-for="(webHookItem, index) in items"
            :key="webHookItem.id"
            :index="index"
            :webhook="webHookItem"
            @edit="openEditPopup"
            @delete="openDeletePopup"
          />
        </template>
      </BaseTable>
    </template>
    <woot-delete-modal
      v-if="apiAndWebhooksEnabled"
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.TITLE')"
      :message="
        $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.MESSAGE', {
          webhookURL: selectedWebHook.url,
        })
      "
      :confirm-text="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.YES')"
      :reject-text="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
